import { useEffect, useId, useRef, useState } from 'react';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Text } from '../atoms/Typography/Text';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { SuggestionRow } from '../capture/SuggestionRow';
import { formatDay, openSuggestions } from '../capture/suggestionFormat';
import { useEntryTaskMutations, useEntryTaskSuggestions } from '../../hooks/useCaptures';
import { useTranslation } from '../../i18n/useTranslation';
import { ApiError } from '../../lib/apiClient';
import type { ApplyItem, SuggestionPayload } from '../../types/capture';
import './DetectedTasks.css';

export interface DetectedTasksProps {
  aquariumId: string;
  entryId: string;
  /** The entry's date (YYYY-MM-DD) — due dates are counted from it. */
  entryDate?: string | null;
  /** Run the (paid) detection once on mount — after saving an entry with signal words. */
  autoDetect?: boolean;
  /** Incremented by the page's "Detect tasks" menu item to run the detection again. */
  detectRequest?: number;
}

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * Suggestions for a typed journal entry (UC-B, wireframe "Detected Tasks"):
 * follow-up tasks and the water-test results written in it. A compact notice
 * "N suggestions found · Review" expands to a pre-checked list; "Save N items"
 * saves the checked ones in one step. Nothing is rendered while there is
 * nothing to show.
 */
export function DetectedTasks({ aquariumId, entryId, entryDate = null, autoDetect = false, detectRequest = 0 }: DetectedTasksProps) {
  const { t, i18n } = useTranslation();
  const listId = useId();
  const firstRowRef = useRef<HTMLDivElement>(null);
  const { data } = useEntryTaskSuggestions(aquariumId, entryId);
  const { detect, apply } = useEntryTaskMutations(aquariumId, entryId);
  const [expanded, setExpanded] = useState(false);
  const [unchecked, setUnchecked] = useState<Record<string, boolean>>({});
  const [edits, setEdits] = useState<Record<string, SuggestionPayload>>({});
  const [announcement, setAnnouncement] = useState('');
  const [detectedOnce, setDetectedOnce] = useState(false);
  const autoRan = useRef(false);
  const lastRequest = useRef(detectRequest);

  const run = (): void => {
    setDetectedOnce(true);
    setAnnouncement('');
    detect.mutate(undefined, {
      onSuccess: (suggestions) => {
        // Found tasks are announced by the notice itself (a polite status); only "none" needs this.
        setAnnouncement(openSuggestions(suggestions).length === 0 ? t('voice.detected.none') : '');
      },
    });
  };

  useEffect(() => {
    if (autoDetect && !autoRan.current) {
      autoRan.current = true;
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, [autoDetect]);

  useEffect(() => {
    if (detectRequest !== lastRequest.current) {
      lastRequest.current = detectRequest;
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the counter changes
  }, [detectRequest]);

  useEffect(() => {
    if (expanded) firstRowRef.current?.querySelector<HTMLInputElement>('input[type="checkbox"]')?.focus();
  }, [expanded]);

  const open = openSuggestions(data ?? []);
  const selected = open.filter((s) => !unchecked[s.id]);
  const backdated = entryDate !== null && entryDate !== todayIso();

  if (detect.isPending) {
    return (
      <LiveRegion busy className="detected-tasks" data-testid="detected-tasks-loading">
        <Text color="muted">{t('voice.detected.detecting')}</Text>
      </LiveRegion>
    );
  }

  if (detect.isError) {
    const limit = detect.error instanceof ApiError && detect.error.code === 'AI_BUDGET_EXCEEDED';
    return (
      <div className="detected-tasks">
        <Alert
          tone={limit ? 'warning' : 'danger'}
          compact
          data-testid="detected-tasks-error"
          action={
            limit ? undefined : (
              <Button variant="secondary" size="sm" onClick={run}>
                {t('voice.detected.retry')}
              </Button>
            )
          }
        >
          {limit ? t('voice.detected.limit') : t('voice.detected.error')}
        </Alert>
      </div>
    );
  }

  if (open.length === 0) {
    const showNone = detectedOnce && detect.isSuccess && !apply.isSuccess;
    return (
      <LiveRegion visuallyHidden={!showNone} className={showNone ? 'detected-tasks' : undefined}>
        {showNone ? <Text color="muted">{t('voice.detected.none')}</Text> : announcement}
      </LiveRegion>
    );
  }

  const create = (): void => {
    const items: ApplyItem[] = selected.map((s) =>
      edits[s.id] ? { id: s.id, payload: edits[s.id] as unknown as Record<string, unknown> } : { id: s.id },
    );
    apply.mutate(
      { items },
      { onSuccess: () => setAnnouncement(t('voice.detected.created', { count: items.length })) },
    );
  };

  return (
    <div className="detected-tasks" data-testid="detected-tasks">
      <Alert
        tone="info"
        live="polite"
        compact
        title={t('voice.detected.foundTitle', { count: open.length })}
        action={
          <div className="detected-tasks__body">
            <Button
              variant="secondary"
              size="sm"
              aria-expanded={expanded}
              aria-controls={listId}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? t('voice.detected.hide') : t('voice.detected.review')}
            </Button>
            <div id={listId} hidden={!expanded} className="detected-tasks__panel" ref={firstRowRef}>
              {backdated && entryDate && (
                <Text variant="caption" color="muted">
                  {t('voice.detected.relativeHint', { date: formatDay(entryDate, i18n.language) })}
                </Text>
              )}
              <ul className="detected-tasks__list">
                {open.map((s) => (
                  <li key={s.id}>
                    <SuggestionRow
                      suggestion={s}
                      payload={edits[s.id] ?? s.payload}
                      checked={!unchecked[s.id]}
                      onCheckedChange={(checked) => setUnchecked((cur) => ({ ...cur, [s.id]: !checked }))}
                      onPayloadChange={(payload) => {
                        setEdits((cur) => ({ ...cur, [s.id]: payload }));
                        setUnchecked((cur) => ({ ...cur, [s.id]: false }));
                      }}
                      showPhrase
                      disabled={apply.isPending}
                    />
                  </li>
                ))}
              </ul>
              {apply.isError && (
                <Alert tone="danger" compact>
                  {t('voice.detected.createFailed')}
                </Alert>
              )}
              <Button variant="primary" size="sm" disabled={selected.length === 0} isLoading={apply.isPending} onClick={create}>
                {t('voice.detected.createSelected', { count: selected.length })}
              </Button>
            </div>
          </div>
        }
      >
        {t('voice.detected.foundBody')}
      </Alert>
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </div>
  );
}
